/**
 * Scholarship Search Page — refreshed design (Feb 2026)
 *
 * Highlights:
 *   - Hero with live stats (total funding, open now, closing this month).
 *   - Featured carousel up top.
 *   - Sticky filter sidebar (left column on desktop).
 *   - Upgraded cards with status badge, deadline countdown, match score
 *     (when logged in as a student).
 *   - "Show scholarships for me" button pre-fills filters from the user
 *     profile + sorts by match score.
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Filter, GraduationCap, MapPin, Calendar, Award, ExternalLink, ChevronRight,
  Loader2, Globe, CheckCircle, ArrowLeft, Star, Send, Sparkles, Clock, Banknote,
  TrendingUp, FlameKindling, ShieldCheck, ChevronLeft, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { scholarshipAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { getErrorMessage } from '@/lib/errors';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_STYLES = {
  open: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  closing_soon: 'bg-amber-100 text-amber-800 border-amber-300',
  closed: 'bg-gray-200 text-gray-600 border-gray-300',
  rolling: 'bg-sky-100 text-sky-700 border-sky-200',
};
const STATUS_LABEL = {
  open: 'Open',
  closing_soon: 'Closing soon',
  closed: 'Closed',
  rolling: 'Rolling',
};

const FUNDING_BADGE = {
  Full: 'bg-emerald-500 text-white',
  Partial: 'bg-amber-500 text-white',
  'Tuition Only': 'bg-sky-500 text-white',
};

const fmtMoney = (gbp) => {
  if (!gbp) return '—';
  if (gbp >= 1_000_000) return `£${(gbp / 1_000_000).toFixed(1)}M`;
  if (gbp >= 1_000) return `£${(gbp / 1_000).toFixed(0)}K`;
  return `£${gbp}`;
};

const matchScoreColor = (s) => {
  if (s >= 75) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  if (s >= 50) return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-rose-600 bg-rose-50 border-rose-200';
};

// ---------------------------------------------------------------------------
// Reusable bits
// ---------------------------------------------------------------------------

function StatTile({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-4 sm:p-5 flex items-start gap-3" data-testid={`scholarship-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`shrink-0 w-10 h-10 rounded-xl ${accent} flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white leading-tight truncate">{value}</p>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{label}</p>
      </div>
    </div>
  );
}

function ScholarshipCard({ scholarship, onView, onApply, highlight }) {
  const status = scholarship.status || 'rolling';
  const days = scholarship.days_remaining;
  return (
    <div
      className={`bg-white dark:bg-slate-800 rounded-2xl border-2 transition-all overflow-hidden flex flex-col h-full group hover:shadow-lg hover:-translate-y-0.5 ${highlight ? 'border-teal-400 shadow-sm shadow-teal-100' : 'border-gray-200 dark:border-slate-700 hover:border-teal-300'}`}
      data-testid={`scholarship-card-${scholarship.id}`}
    >
      {/* Top strip */}
      <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`${FUNDING_BADGE[scholarship.funding_type] || 'bg-gray-500 text-white'} border-0`}>
            {scholarship.funding_type}
          </Badge>
          {scholarship.featured && (
            <Badge className="bg-violet-100 text-violet-700 border-violet-200">
              <Star className="w-3 h-3 mr-1 fill-violet-500" />
              Featured
            </Badge>
          )}
          <Badge className={`${STATUS_STYLES[status]} border`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current mr-1.5 inline-block animate-pulse" />
            {STATUS_LABEL[status]}
          </Badge>
        </div>
        {typeof scholarship.match_score === 'number' && (
          <div className={`text-xs font-bold px-2 py-1 rounded-lg border ${matchScoreColor(scholarship.match_score)}`} title="How well this scholarship matches your profile">
            {scholarship.match_score}% match
          </div>
        )}
      </div>

      {/* Body */}
      <div className="px-5 pb-4 flex-1">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white line-clamp-2 leading-snug group-hover:text-teal-600 transition-colors">
          {scholarship.name}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{scholarship.provider}</p>

        <div className="mt-3 space-y-1.5 text-xs text-gray-600 dark:text-gray-300">
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-blue-500 shrink-0" />
            <span className="truncate">{scholarship.countries?.join(', ')}</span>
          </div>
          <div className="flex items-center gap-2">
            <GraduationCap className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            <span className="truncate">{scholarship.degree_levels?.join(', ')}</span>
          </div>
          {scholarship.funding_amount && (
            <div className="flex items-center gap-2">
              <Banknote className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <span className="truncate">{scholarship.funding_amount}</span>
            </div>
          )}
          {days !== null && days !== undefined && status !== 'closed' && status !== 'rolling' && (
            <div className="flex items-center gap-2">
              <Clock className={`w-3.5 h-3.5 shrink-0 ${status === 'closing_soon' ? 'text-rose-500' : 'text-teal-500'}`} />
              <span className={`font-medium ${status === 'closing_soon' ? 'text-rose-600' : 'text-gray-700 dark:text-gray-200'}`}>
                {days <= 0 ? 'Deadline today' : `${days} day${days === 1 ? '' : 's'} to apply`}
              </span>
            </div>
          )}
        </div>

        {scholarship.match_reasons && scholarship.match_reasons.length > 0 && (
          <div className="mt-3 p-2.5 rounded-lg bg-emerald-50/80 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-1">Why it fits you</p>
            <ul className="space-y-0.5">
              {scholarship.match_reasons.slice(0, 2).map((r, i) => (
                <li key={i} className="text-[11px] text-emerald-800 dark:text-emerald-200 flex items-start gap-1">
                  <CheckCircle className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100 dark:border-slate-700 p-3 flex gap-2 bg-gray-50/60 dark:bg-slate-900/40">
        <Button variant="outline" className="flex-1 h-9 text-xs" onClick={() => onView(scholarship)} data-testid={`scholarship-view-${scholarship.id}`}>
          Details
        </Button>
        <Button
          className={`flex-1 h-9 text-xs font-semibold ${status === 'closed' ? 'bg-gray-400 hover:bg-gray-400 text-white cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700 text-white'}`}
          onClick={() => status !== 'closed' && onApply(scholarship)}
          disabled={status === 'closed'}
          data-testid={`scholarship-apply-${scholarship.id}`}
        >
          <Send className="w-3.5 h-3.5 mr-1.5" />
          Apply
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ScholarshipSearchPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [scholarships, setScholarships] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingReco, setLoadingReco] = useState(false);
  const [total, setTotal] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('all');
  const [degreeLevel, setDegreeLevel] = useState('all');
  const [fundingType, setFundingType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [matchedMode, setMatchedMode] = useState(false);

  // Modals
  const [selectedScholarship, setSelectedScholarship] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [interestMessage, setInterestMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Carousel
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Initial stats load
  useEffect(() => {
    (async () => {
      try {
        const s = await scholarshipAPI.getStats();
        setStats(s.data);
      } catch (e) {
        console.warn('stats failed', e);
      }
    })();
  }, []);

  // Load scholarships whenever filters change
  useEffect(() => {
    if (matchedMode && user?.role === 'student') return; // recommended mode handled separately
    loadScholarships();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, degreeLevel, fundingType, matchedMode]);

  const loadScholarships = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (country && country !== 'all') params.country = country;
      if (degreeLevel && degreeLevel !== 'all') params.degree_level = degreeLevel;
      if (fundingType && fundingType !== 'all') params.funding_type = fundingType;
      const res = await scholarshipAPI.getAll(params);
      setScholarships(res.data.scholarships || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load scholarships'));
    } finally {
      setLoading(false);
    }
  };

  const enableMatchedMode = async () => {
    if (!user) {
      toast.error('Please log in to see scholarships matched to your profile');
      navigate('/login');
      return;
    }
    setMatchedMode(true);
    setLoadingReco(true);
    try {
      const res = await scholarshipAPI.getRecommended(24);
      setRecommended(res.data.scholarships || []);
      toast.success(`Found ${res.data.scholarships?.length || 0} scholarships matched to your profile`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load personalised matches'));
      setMatchedMode(false);
    } finally {
      setLoadingReco(false);
    }
  };

  const clearMatchedMode = () => {
    setMatchedMode(false);
    setRecommended([]);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (matchedMode) clearMatchedMode();
    loadScholarships();
  };

  const handleViewDetails = async (scholarship) => {
    try {
      const res = await scholarshipAPI.getById(scholarship.id);
      setSelectedScholarship(res.data);
      setShowDetailModal(true);
    } catch {
      toast.error('Failed to load scholarship details');
    }
  };

  const handleExpressInterest = (scholarship) => {
    if (!user) {
      toast.error('Please log in to apply');
      navigate('/login');
      return;
    }
    setSelectedScholarship(scholarship);
    setInterestMessage('');
    setShowInterestModal(true);
  };

  const submitInterest = async () => {
    if (!selectedScholarship) return;
    setSubmitting(true);
    try {
      await scholarshipAPI.expressInterest(selectedScholarship.id, interestMessage || null);
      toast.success('Your application has been submitted. Our team will contact you shortly.');
      setShowInterestModal(false);
      setSelectedScholarship(null);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to submit application'));
    } finally {
      setSubmitting(false);
    }
  };

  // Filter list locally by status + search (server already filtered by country/level/funding)
  const sourceList = matchedMode ? recommended : scholarships;
  const filteredList = useMemo(() => {
    let list = sourceList;
    if (statusFilter !== 'all') list = list.filter((s) => (s.status || 'rolling') === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((s) =>
        s.name?.toLowerCase().includes(q) ||
        s.provider?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [sourceList, statusFilter, search]);

  const featured = useMemo(() => sourceList.filter((s) => s.featured).slice(0, 6), [sourceList]);
  const carouselCard = featured[carouselIndex];

  const resetFilters = () => {
    setSearch('');
    setCountry('all');
    setDegreeLevel('all');
    setFundingType('all');
    setStatusFilter('all');
    clearMatchedMode();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      <Navbar />

      {/* HERO */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 dark:hover:text-white mb-5 text-sm transition-colors"
          data-testid="scholarship-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Headline */}
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 text-amber-800 dark:text-amber-300 px-3 py-1.5 rounded-full text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              {stats?.total_funding_gbp ? `${fmtMoney(stats.total_funding_gbp)}+ in scholarships` : 'Verified opportunities'}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white leading-tight">
              Fund your study abroad,<br />
              <span className="bg-gradient-to-r from-teal-500 to-emerald-500 bg-clip-text text-transparent">
                stress-free.
              </span>
            </h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base max-w-xl">
              Browse {stats?.total_scholarships ?? '18+'} verified, government-backed and university scholarships.
              Each entry shows live deadline status so you only see opportunities you can still apply to.
            </p>
            {user?.role === 'student' && (
              !matchedMode ? (
                <Button
                  onClick={enableMatchedMode}
                  className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:opacity-90 text-white font-semibold shadow-sm h-11 px-6"
                  data-testid="match-scholarships-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Show scholarships matched to me
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={clearMatchedMode}
                  className="border-teal-300 text-teal-700 hover:bg-teal-50 h-11"
                  data-testid="clear-matched-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear personalised view
                </Button>
              )
            )}
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3">
            <StatTile icon={Award} label="Scholarships" value={stats?.total_scholarships ?? '—'} accent="bg-gradient-to-br from-violet-500 to-purple-500" />
            <StatTile icon={Banknote} label="Total funding pool" value={fmtMoney(stats?.total_funding_gbp)} accent="bg-gradient-to-br from-emerald-500 to-teal-500" />
            <StatTile icon={CheckCircle} label="Open right now" value={stats?.open_now ?? '—'} accent="bg-gradient-to-br from-sky-500 to-blue-500" />
            <StatTile icon={FlameKindling} label="Closing in 30 days" value={stats?.closing_soon ?? '—'} accent="bg-gradient-to-br from-amber-500 to-orange-500" />
          </div>
        </div>

        {/* Featured carousel */}
        {featured.length > 0 && (
          <div className="mb-8" data-testid="scholarship-featured-carousel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500 fill-amber-400" />
                Featured this month
              </h2>
              {featured.length > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCarouselIndex((i) => (i - 1 + featured.length) % featured.length)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-600 dark:text-gray-300 transition-colors"
                    aria-label="Previous"
                  ><ChevronLeft className="w-5 h-5" /></button>
                  <span className="text-xs text-gray-500 px-2">{carouselIndex + 1} / {featured.length}</span>
                  <button
                    onClick={() => setCarouselIndex((i) => (i + 1) % featured.length)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-600 dark:text-gray-300 transition-colors"
                    aria-label="Next"
                  ><ChevronRight className="w-5 h-5" /></button>
                </div>
              )}
            </div>
            {carouselCard && (
              <div className="rounded-2xl bg-gradient-to-br from-teal-600 via-emerald-600 to-cyan-600 dark:from-teal-700 dark:to-emerald-700 p-6 sm:p-8 text-white relative overflow-hidden shadow-md">
                <div className="absolute -right-12 -top-12 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none" />
                <div className="absolute -left-8 -bottom-8 w-40 h-40 bg-amber-300/20 rounded-full blur-2xl pointer-events-none" />
                <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                  <div className="md:col-span-2">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">{carouselCard.funding_type}</Badge>
                      <Badge className="bg-amber-400/90 text-amber-950 border-0">
                        {STATUS_LABEL[carouselCard.status] || 'Rolling'}
                      </Badge>
                      {carouselCard.next_deadline_iso && (
                        <Badge className="bg-white/20 text-white border-0">
                          <Calendar className="w-3 h-3 mr-1" />
                          {carouselCard.deadline || carouselCard.next_deadline_iso}
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold mb-1">{carouselCard.name}</h3>
                    <p className="text-white/80 text-sm mb-3">{carouselCard.provider}</p>
                    <p className="text-white/90 text-sm line-clamp-2 mb-4 max-w-xl">{carouselCard.description}</p>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        className="bg-white text-teal-700 hover:bg-amber-50 font-semibold"
                        onClick={() => handleViewDetails(carouselCard)}
                        data-testid="carousel-view-details"
                      >
                        View Details <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                      <Button
                        variant="outline"
                        className="bg-transparent text-white border-white/40 hover:bg-white/15 hover:text-white font-semibold"
                        onClick={() => handleExpressInterest(carouselCard)}
                        data-testid="carousel-apply"
                      >
                        <Send className="w-4 h-4 mr-1.5" /> Apply via H E Direct
                      </Button>
                    </div>
                  </div>
                  <div className="hidden md:flex flex-col items-center justify-center bg-white/10 backdrop-blur rounded-2xl p-5 border border-white/20">
                    <p className="text-xs uppercase tracking-widest text-white/70">Estimated value</p>
                    <p className="text-3xl font-bold mt-1">{fmtMoney(carouselCard.est_funding_gbp)}</p>
                    {typeof carouselCard.days_remaining === 'number' && carouselCard.status !== 'closed' && (
                      <p className="text-xs text-white/80 mt-3 text-center">
                        <Clock className="inline w-3 h-3 mr-1" />
                        {carouselCard.days_remaining <= 0 ? 'Closes today' : `${carouselCard.days_remaining} days remaining`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* MAIN: sticky sidebar + cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Filter sidebar */}
          <aside className="lg:sticky lg:top-24 lg:self-start space-y-4" data-testid="scholarship-filter-sidebar">
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100 dark:border-slate-700">
                <Filter className="w-4 h-4 text-teal-600" />
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">Filters</h3>
                <button
                  onClick={resetFilters}
                  className="ml-auto text-xs text-gray-500 hover:text-teal-600 transition-colors"
                  data-testid="reset-filters-btn"
                >Reset</button>
              </div>

              <form onSubmit={handleSearch} className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by name, provider…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-9 text-sm"
                    data-testid="scholarship-search-input"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Destination</label>
                  <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-country"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All countries</SelectItem>
                      <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                      <SelectItem value="United States">United States</SelectItem>
                      <SelectItem value="Australia">Australia</SelectItem>
                      <SelectItem value="Canada">Canada</SelectItem>
                      <SelectItem value="Germany">Germany</SelectItem>
                      <SelectItem value="France">France</SelectItem>
                      <SelectItem value="Japan">Japan</SelectItem>
                      <SelectItem value="China">China</SelectItem>
                      <SelectItem value="Switzerland">Switzerland</SelectItem>
                      <SelectItem value="Turkey">Turkey</SelectItem>
                      <SelectItem value="Hungary">Hungary</SelectItem>
                      <SelectItem value="Multiple European Countries">Multiple European</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Degree</label>
                  <Select value={degreeLevel} onValueChange={setDegreeLevel}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-degree"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All levels</SelectItem>
                      <SelectItem value="Undergraduate">Undergraduate</SelectItem>
                      <SelectItem value="Masters">Masters</SelectItem>
                      <SelectItem value="PhD">PhD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Funding</label>
                  <Select value={fundingType} onValueChange={setFundingType}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-funding"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All funding types</SelectItem>
                      <SelectItem value="Full">Fully funded</SelectItem>
                      <SelectItem value="Partial">Partial</SelectItem>
                      <SelectItem value="Tuition Only">Tuition only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Status</label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="closing_soon">Closing soon</SelectItem>
                      <SelectItem value="rolling">Rolling</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white text-sm" data-testid="apply-filters-btn">
                  Apply filters
                </Button>
              </form>
            </div>

            {/* Trust panel */}
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-4">
              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 text-sm font-semibold mb-2">
                <ShieldCheck className="w-4 h-4" /> Verified data
              </div>
              <p className="text-xs text-emerald-800 dark:text-emerald-200/90 leading-relaxed">
                Every scholarship is verified against the official source. Deadlines are computed live — so you never see expired opportunities marked as open.
              </p>
            </div>
          </aside>

          {/* Results grid */}
          <main>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="results-count">
                {(loading || loadingReco) ? 'Loading…' :
                  matchedMode
                    ? `${filteredList.length} scholarship${filteredList.length === 1 ? '' : 's'} matched to your profile`
                    : `Showing ${filteredList.length} of ${total} scholarship${total === 1 ? '' : 's'}`
                }
              </p>
              {matchedMode && (
                <Badge className="bg-teal-100 text-teal-700 border-teal-200">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Sorted by best match
                </Badge>
              )}
            </div>

            {(loading || loadingReco) ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 h-64 animate-pulse" />
                ))}
              </div>
            ) : filteredList.length === 0 ? (
              <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-12 text-center">
                <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">No scholarships match these filters</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Try clearing some filters or switching the destination.</p>
                <Button variant="outline" onClick={resetFilters}>Clear all filters</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="scholarships-grid">
                {filteredList.map((s, idx) => (
                  <ScholarshipCard
                    key={s.id}
                    scholarship={s}
                    onView={handleViewDetails}
                    onApply={handleExpressInterest}
                    highlight={matchedMode && idx < 3}
                  />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto">
          {selectedScholarship && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge className={`${FUNDING_BADGE[selectedScholarship.funding_type] || 'bg-gray-500'} text-white border-0`}>
                    {selectedScholarship.funding_type}
                  </Badge>
                  {selectedScholarship.featured && (
                    <Badge className="bg-violet-100 text-violet-700">
                      <Star className="w-3 h-3 mr-1 fill-violet-500" /> Featured
                    </Badge>
                  )}
                  <Badge className={`${STATUS_STYLES[selectedScholarship.status || 'rolling']} border`}>
                    {STATUS_LABEL[selectedScholarship.status || 'rolling']}
                  </Badge>
                </div>
                <DialogTitle className="text-xl sm:text-2xl text-gray-900 dark:text-white">
                  {selectedScholarship.name}
                </DialogTitle>
                <p className="text-sm text-gray-500 dark:text-gray-400">{selectedScholarship.provider}</p>
              </DialogHeader>

              <div className="space-y-5 py-2">
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{selectedScholarship.description}</p>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-1">Destinations</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedScholarship.countries?.map((c, i) => (
                        <Badge key={i} variant="outline">{c}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-1">Levels</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedScholarship.degree_levels?.map((d, i) => (
                        <Badge key={i} variant="outline">{d}</Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {selectedScholarship.funding_amount && (
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-1">Funding amount</p>
                    <p className="text-emerald-600 dark:text-emerald-400 font-semibold">{selectedScholarship.funding_amount}</p>
                  </div>
                )}

                <div>
                  <p className="text-xs font-semibold uppercase text-gray-500 mb-1">Eligibility</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{selectedScholarship.eligibility}</p>
                </div>

                {selectedScholarship.benefits?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-1">What's included</p>
                    <ul className="space-y-1">
                      {selectedScholarship.benefits.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />{b}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedScholarship.requirements?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 mb-1">Requirements</p>
                    <ul className="space-y-1">
                      {selectedScholarship.requirements.map((r, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <ChevronRight className="w-4 h-4 text-teal-500 mt-0.5 shrink-0" />{r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedScholarship.next_deadline_iso && (
                  <div className={`p-3 rounded-xl border ${selectedScholarship.status === 'closing_soon' ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-teal-50 border-teal-200 text-teal-700'}`}>
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Calendar className="w-4 h-4" />
                      Application deadline: {selectedScholarship.deadline}
                      {typeof selectedScholarship.days_remaining === 'number' && selectedScholarship.days_remaining >= 0 && (
                        <span className="ml-auto text-xs">{selectedScholarship.days_remaining} days remaining</span>
                      )}
                    </div>
                  </div>
                )}

                {selectedScholarship.official_website && (
                  <a
                    href={selectedScholarship.official_website}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    <Globe className="w-4 h-4" />
                    Visit official website
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
                <Button
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={() => { setShowDetailModal(false); handleExpressInterest(selectedScholarship); }}
                  disabled={selectedScholarship.status === 'closed'}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Apply via H E Direct
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Interest / Apply Modal */}
      <Dialog open={showInterestModal} onOpenChange={setShowInterestModal}>
        <DialogContent className="max-w-md bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">Apply for scholarship</DialogTitle>
          </DialogHeader>
          {selectedScholarship && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-gray-50 dark:bg-slate-900/40 rounded-lg border border-gray-100 dark:border-slate-700">
                <p className="font-semibold text-gray-900 dark:text-white text-sm">{selectedScholarship.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{selectedScholarship.provider}</p>
              </div>
              <div className="p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
                <p className="text-xs text-teal-700 dark:text-teal-300">
                  <strong>What happens next:</strong> Your application is sent to our H E Direct team.
                  A counsellor will email/call you within 24 hours to guide you through the official scholarship application process.
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1.5">
                  Anything we should know? (optional)
                </label>
                <Textarea
                  value={interestMessage}
                  onChange={(e) => setInterestMessage(e.target.value)}
                  placeholder="Your background, why this scholarship, any questions…"
                  className="min-h-[90px] text-sm"
                  data-testid="interest-message-textarea"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInterestModal(false)} disabled={submitting}>Cancel</Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700 text-white"
              onClick={submitInterest}
              disabled={submitting}
              data-testid="submit-interest-btn"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</> : <><Send className="w-4 h-4 mr-2" />Submit application</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
